import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Briefcase, 
  Plus, 
  Search,
  UserCheck,
  Mail,
  Phone,
  Users,
  Link,
  Loader2,
  Clock,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const MyDealersPage = () => {
  const { token, user } = useAuth();
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    dealer_code: '',
    name: '',
    email: '',
    mobile: '',
    status: 'Active',
  });
  const [mobileError, setMobileError] = useState('');

  const fetchDealers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/dealers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDealers(data);
      } else {
        toast.error('Failed to load dealers');
      }
    } catch (err) {
      toast.error('Error loading dealers');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  const validateMobile = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length > 0 && cleanValue.length !== 10) {
      setMobileError('Enter 10 digit mobile number');
    } else {
      setMobileError('');
    }
    return cleanValue;
  };

  const handleAddDealer = async () => {
    if (!formData.dealer_code || !formData.name) {
      toast.error('Dealer Code and Name are required');
      return;
    }

    // Validate mobile
    if (formData.mobile) {
      const cleanMobile = formData.mobile.replace(/\D/g, '');
      if (cleanMobile.length !== 10) {
        toast.error('Invalid mobile number. Enter a 10-digit mobile number.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/dealers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          mobile: formData.mobile ? formData.mobile.replace(/\D/g, '') : '',
        }),
      });

      if (res.ok) {
        toast.success('Dealer created. Pending approval from Principal.');
        setShowAddDialog(false);
        setFormData({ dealer_code: '', name: '', email: '', mobile: '', status: 'Active' });
        fetchDealers();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create dealer');
      }
    } catch (err) {
      toast.error('Error creating dealer');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredDealers = dealers.filter((d) => {
    const matchesSearch =
      d.dealer_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (d.email && d.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || d.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: dealers.length,
    active: dealers.filter((d) => d.status === 'Active').length,
    pending: dealers.filter((d) => d.approval_status === 'pending_approval').length,
    totalClients: dealers.reduce((sum, d) => sum + (d.client_count || 0), 0),
  };

  // Approval status badge
  const getApprovalBadge = (approvalStatus) => {
    if (!approvalStatus || approvalStatus === 'approved') return null;
    if (approvalStatus === 'pending_approval') {
      return (
        <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs ml-2">
          <Clock className="w-3 h-3 mr-1" />
          Pending Approval
        </Badge>
      );
    }
    if (approvalStatus === 'rejected') {
      return (
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-xs ml-2">
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="my-dealers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Briefcase className="w-7 h-7 text-green-400" />
            My Dealers
          </h1>
          <p className="text-slate-400 mt-1">Manage and add dealers under your supervision</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)} className="bg-green-600 hover:bg-green-700" data-testid="add-dealer-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Dealer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <Briefcase className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-xs text-slate-400">Total Dealers</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/20">
                <UserCheck className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.active}</p>
                <p className="text-xs text-slate-400">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.pending}</p>
                <p className="text-xs text-slate-400">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Users className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{stats.totalClients}</p>
                <p className="text-xs text-slate-400">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="card-dark">
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by code, name, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 input-vault"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 input-vault">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Dealer Table */}
      <Card className="card-dark">
        <CardHeader>
          <CardTitle className="text-white">Dealer Directory</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : filteredDealers.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No dealers found</p>
              <p className="text-sm mt-2">Click "Add Dealer" to create your first dealer</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-300">Code</TableHead>
                    <TableHead className="text-slate-300">Name</TableHead>
                    <TableHead className="text-slate-300">Contact</TableHead>
                    <TableHead className="text-slate-300">Clients</TableHead>
                    <TableHead className="text-slate-300">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDealers.map((dealer) => (
                    <TableRow key={dealer.dealer_code} className="border-slate-700">
                      <TableCell className="text-white font-mono font-medium">{dealer.dealer_code}</TableCell>
                      <TableCell>
                        <div className="flex items-center flex-wrap gap-1">
                          <span className="text-white">{dealer.name}</span>
                          {getApprovalBadge(dealer.approval_status)}
                        </div>
                        {dealer.added_by_name && dealer.approval_status === 'pending_approval' && (
                          <div className="text-xs text-slate-500 mt-1">
                            Added by: {dealer.added_by_name}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-300">
                        <div className="space-y-1">
                          {dealer.email && (
                            <div className="flex items-center gap-1 text-sm">
                              <Mail className="w-3 h-3" /> {dealer.email}
                            </div>
                          )}
                          {dealer.mobile && (
                            <div className="flex items-center gap-1 text-sm">
                              <Phone className="w-3 h-3" /> {dealer.mobile}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-white font-medium">{dealer.client_count || 0}</TableCell>
                      <TableCell>
                        <Badge
                          className={
                            dealer.status === 'Active'
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }
                        >
                          {dealer.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Dealer Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Add New Dealer
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new dealer under your supervision. The dealer will be pending approval from Principal.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Dealer Code *</Label>
                <Input
                  value={formData.dealer_code}
                  onChange={(e) => setFormData({ ...formData, dealer_code: e.target.value.toUpperCase() })}
                  placeholder="e.g., D001"
                  className="input-vault"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full name"
                  className="input-vault"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="input-vault"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Mobile (10 digits)</Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => {
                    const cleanValue = validateMobile(e.target.value);
                    setFormData({ ...formData, mobile: cleanValue.slice(0, 10) });
                  }}
                  placeholder="9876543210"
                  maxLength={10}
                  className="input-vault"
                />
                {mobileError && (
                  <p className="text-xs text-amber-400">{mobileError}</p>
                )}
              </div>
            </div>
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-400 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                This dealer will require approval from Principal before becoming active.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-slate-600">
              Cancel
            </Button>
            <Button 
              onClick={handleAddDealer} 
              disabled={submitting || !!mobileError} 
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Dealer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyDealersPage;
